import { Ioc } from "@core/ioc/ioc";
import { clearAllThumbnails } from "@models/storage/thumbnailCache";
import { clearProfilePicCache } from "@models/storage/profilePicCache";
import { clearProjectBinaryCacheIndex } from "@models/storage/projectBinaryCache";
import { clearMaterialTextureCache } from "@models/materials/materialTextureCache";
import { clearWorkflowPresenceCache } from "@models/workflow/workflowPresenceCache";
import { readSuppressedIndex, K_SUPPRESSED_INDEX } from "@models/notifications/ConfirmationModel";
import type { GltfAssetCache } from "@runtime/assets/gltfAssetCache";
import { GLTF_ASSET_CACHE } from "@runtime/assets/gltfAssetCache";
import type { CacheClearResult, IAppCacheModel } from "./IAppCacheModel";

/**
 * Browser implementation of {@link IAppCacheModel} — see that file for why the
 * web's notion of "cache" differs from Unity's on-disk project directory.
 */
export class AppCacheModel implements IAppCacheModel {
  async getCacheSizeAsync(): Promise<number | null> {
    // `navigator.storage.estimate()` alone is NOT enough: measured against the
    // dev build it reports usage 0 with an empty `usageDetails` while
    // localStorage holds ~4.8 KB, so Chrome is not counting localStorage in the
    // estimate. Reporting it verbatim would peg the readout at "0B" forever.
    // Sum both, and treat "neither is available" as Unity's "Unknown".
    const estimate = await storageEstimateBytes();
    const local = localStorageBytes();
    if (estimate === null && local === null) return null;
    return (estimate ?? 0) + (local ?? 0);
  }

  async clearCacheAsync(): Promise<CacheClearResult> {
    // Profile pictures and material textures are counted with thumbnails: all
    // three are cached S3 image blobs the user would expect "Clear Cache" to
    // drop, and none is state worth preserving.
    const thumbnails =
      clearAllThumbnails() + clearProfilePicCache() + clearMaterialTextureCache();

    // Not counted with them: this holds one boolean per project ("does it have
    // a workflow"), not a cached blob, so folding it into a blob tally would
    // misreport what was freed. Dropped all the same — it is the one cached
    // answer another client can invalidate behind our back.
    clearWorkflowPresenceCache();

    // The glTF cache is only registered once a viewport has mounted, so app
    // settings reached straight from the projects grid legitimately finds none.
    const assets = Ioc.isRegistered<GltfAssetCache>(GLTF_ASSET_CACHE)
      ? Ioc.resolve<GltfAssetCache>(GLTF_ASSET_CACHE).clear()
      : 0;

    const preferences = clearOneShotPreferences();

    // Cache Storage holds the persistent asset-binary cache — the GLB bytes
    // `GltfAssetCache` reads instead of re-downloading them — plus anything a
    // service worker ever caches. This is where "Clear Cache" frees real
    // gigabytes; the counts above deliberately do not include it, because
    // `CacheClearResult` counts entries and this bucket's unit is bytes.
    await clearCacheStorage();
    // The bucket's LRU index lives in localStorage and would otherwise survive
    // the bytes it describes, mis-sizing the next eviction pass. `reconcile`
    // would heal it eventually; dropping it here is exact.
    clearProjectBinaryCacheIndex();

    return { thumbnails, assets, preferences };
  }
}

/**
 * Reset the one-shot "don't show this again" dismissals — the web's equivalent
 * of `ReleaseNotesExtensions.ClearReleaseNotesPrefs()`, which Unity chains onto
 * its cache clear.
 *
 * Deliberately does NOT touch real preferences (`Campfire.AI.*`,
 * `Campfire.Workflow.DevMode`, the debug flags) or anything auth-related.
 * Unity's cache clear leaves its `PlayerPrefs` alone too — wiping a user's
 * settings is not what "clear cache" promises.
 */
function clearOneShotPreferences(): number {
  let removed = 0;
  try {
    for (const key of readSuppressedIndex()) {
      if (window.localStorage.getItem(key) !== null) {
        window.localStorage.removeItem(key);
        removed++;
      }
    }
    window.localStorage.removeItem(K_SUPPRESSED_INDEX);
  } catch {
    // Private-mode / disabled storage: report what we managed.
  }
  return removed;
}

/** IndexedDB / Cache Storage usage, or `null` when the browser won't say. */
async function storageEstimateBytes(): Promise<number | null> {
  // Absent on non-secure origins and older Safari.
  if (!navigator.storage?.estimate) return null;
  try {
    const { usage } = await navigator.storage.estimate();
    return typeof usage === "number" ? usage : null;
  } catch {
    return null;
  }
}

/**
 * Approximate localStorage footprint: keys + values as UTF-16 code units.
 * Browsers do not expose a real figure, and this is the same approximation
 * every storage-quota shim uses.
 */
function localStorageBytes(): number | null {
  try {
    let total = 0;
    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i);
      if (key === null) continue;
      total += key.length + (window.localStorage.getItem(key)?.length ?? 0);
    }
    return total;
  } catch {
    // Private-mode / disabled storage.
    return null;
  }
}

async function clearCacheStorage(): Promise<void> {
  if (!globalThis.caches) return;
  try {
    const keys = await caches.keys();
    await Promise.all(keys.map((k) => caches.delete(k)));
  } catch {
    // Non-fatal — the rest of the clear already happened.
  }
}

/**
 * Port of `AppSettingsViewModel.ConvertToHumanReadableSize` (`:331`) — same
 * unit table and the same `{0:0.#}` one-decimal-max formatting, so "1.5MB" and
 * "0B" read identically to the desktop app.
 */
export function formatCacheSize(bytes: number): string {
  const sizes = ["B", "KB", "MB", "GB", "TB"];
  let len = bytes;
  let order = 0;
  while (len >= 1024 && order < sizes.length - 1) {
    order++;
    len = len / 1024;
  }
  // `0:0.#` = at most one decimal, trailing ".0" dropped.
  const rounded = Math.round(len * 10) / 10;
  return `${rounded}${sizes[order]}`;
}
